import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { EntryContext, JourneyName, JourneyStep } from '@/utils/analytics/taxonomy';
import { sanitizePagePath } from '@/utils/analytics/taxonomy';
import type { OnboardingPath } from '@/utils/analytics/types';

/**
 * Carries onboarding attribution from the point the user commits to a path (Get Started,
 * or the exploration-choice modal at the end of the own-data flow) to the product page
 * they land on, so that page's page_viewed can report journey_step=platform_entry — the
 * signal the User Profile sheet defines onboarding_completed_at against.
 *
 * The attribution travels in the URL rather than in shared state so an ordinary visit to
 * Explore or the Query Editor can never inherit a stale onboarding entry_context. What
 * cannot fit in the URL — when the path was chosen, whether platform_entry has already
 * been claimed — lives in sessionStorage: per-tab, and it survives the refresh that
 * module state would not.
 */

export const JOURNEY_ENTRY_PARAM = 'entry_context';

const PATH_SELECTED_AT_KEY = 'tg_analytics_onboarding_path_selected_at';
const PLATFORM_ENTRY_CLAIM_KEY = 'tg_analytics_platform_entry_claim';

const ONBOARDING_PATH_BY_ENTRY_CONTEXT: Record<string, OnboardingPath> = {
  onboarding_preloaded_solution: 'preloaded_solution',
  onboarding_own_data: 'own_data',
};

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Private-mode Safari and hardened browser settings throw on access. Losing the
    // supplementary properties is acceptable; losing the page_viewed is not.
    return null;
  }
}

function writeSession(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

/** Append the onboarding entry context to a destination URL built by an onboarding exit. */
export function withJourneyEntry(url: string, entryContext: EntryContext): string {
  return `${url}${url.includes('?') ? '&' : '?'}${JOURNEY_ENTRY_PARAM}=${entryContext}`;
}

/**
 * Record the moment the user commits to an onboarding path. time_since_path_selected_seconds
 * is defined as the elapsed time from that commit to the usable destination, so it must be
 * stamped once, at the Get Started submit — not at each stage of the flow.
 */
export function markOnboardingPathSelected(now: number = Date.now()) {
  writeSession(PATH_SELECTED_AT_KEY, `${now}`);
}

function timeSincePathSelectedSeconds(now: number = Date.now()): number | undefined {
  const raw = readSession(PATH_SELECTED_AT_KEY);
  if (!raw) {
    return undefined;
  }
  const selectedAt = Number(raw);
  if (!Number.isFinite(selectedAt) || selectedAt <= 0 || now < selectedAt) {
    return undefined;
  }
  return Math.round((now - selectedAt) / 1000);
}

// Remembering the answer, not just the fact, keeps the claim idempotent for a single
// mount: React StrictMode replays lazy initializers in dev, and a re-render must not turn
// a granted claim into a denied one.
let lastClaim: { key: string; granted: boolean } | null = null;

/**
 * Drops the in-memory memo while leaving the persisted claim in place — exactly what a page
 * reload does, and the only way a test can exercise the refresh path in one module instance.
 */
export function clearJourneyEntryMemo() {
  lastClaim = null;
}

/** Exposed for tests; a real session only ever ends a claim by closing the tab. */
export function resetJourneyEntryState() {
  lastClaim = null;
  try {
    window.sessionStorage.removeItem(PATH_SELECTED_AT_KEY);
    window.sessionStorage.removeItem(PLATFORM_ENTRY_CLAIM_KEY);
  } catch {
    // ignore
  }
}

/**
 * platform_entry marks onboarding *completion*, so it must be emitted once per onboarding
 * — not once per refresh of the destination URL, which still carries the entry_context.
 * The claim is keyed by context + path so the three own-data exploration destinations are
 * each claimable, but reloading any one of them is not.
 */
function claimPlatformEntry(claimKey: string): boolean {
  if (lastClaim?.key === claimKey) {
    return lastClaim.granted;
  }

  const claimed = (readSession(PLATFORM_ENTRY_CLAIM_KEY) ?? '').split('|').filter(Boolean);
  const granted = !claimed.includes(claimKey);
  if (granted) {
    writeSession(PLATFORM_ENTRY_CLAIM_KEY, [...claimed, claimKey].join('|'));
  }
  lastClaim = { key: claimKey, granted };
  return granted;
}

export type JourneyEntryProps = {
  entry_context?: EntryContext;
  journey_name?: JourneyName;
  journey_step?: JourneyStep;
  onboarding_path?: OnboardingPath;
  time_since_path_selected_seconds?: number;
};

export function resolveJourneyEntry(entryContext: string | null, pagePath?: string): JourneyEntryProps {
  if (!entryContext) {
    return {};
  }

  const onboardingPath = ONBOARDING_PATH_BY_ENTRY_CONTEXT[entryContext];
  if (!onboardingPath) {
    return {};
  }

  const props: JourneyEntryProps = {
    entry_context: entryContext as EntryContext,
    journey_name: 'onboarding',
    onboarding_path: onboardingPath,
  };

  // The keys are omitted rather than set to undefined so a caller can spread this object
  // over its own defaults without blanking them on a re-entry that no longer qualifies.
  if (claimPlatformEntry(`${entryContext}:${pagePath ?? ''}`)) {
    props.journey_step = 'platform_entry';
    const elapsed = timeSincePathSelectedSeconds();
    if (elapsed !== undefined) {
      props.time_since_path_selected_seconds = elapsed;
    }
  }

  return props;
}

/**
 * Spread the result into usePageViewed on any page that is a qualified onboarding
 * destination. Returns an empty object — and so changes nothing — for ordinary visits.
 */
export function useJourneyEntry(): JourneyEntryProps {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const entryContext = searchParams.get(JOURNEY_ENTRY_PARAM);
  const pagePath = sanitizePagePath(location.pathname);

  // The claim is a side effect, so it must not re-run on every render. usePageViewed also
  // compares these values by identity in its effect deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => resolveJourneyEntry(entryContext, pagePath), [entryContext, pagePath]);
}
